import React, { FC, useState } from 'react';

import { gql } from '@apollo/client';
import { Loader, PublicError } from '@modernloop/shared/helper-components';
import { logError } from '@modernloop/shared/utils';

import {
  ActivityLogEntityType,
  ActivityType,
  useActivityLogCreatedSubscription,
  useTaskDetailsActivityLogsListQuery,
  useTaskUpdatedSubscription,
} from 'src/generated/mloop-graphql';

import { PAGER_DEFAULT, PAGE_SIZE_100 } from 'src/constants';

import { AGENT_ACTIVITY_LOG_SAFETY_POLL_INTERVAL_MS } from './agentActivityLogSafetyPoll';

import { SCHEDULE_TASK_RELATED_QUERY_NAMES } from 'src/hooks/useRefetchScheduleTaskQuery';

import ActivityLogsList from '.';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ActivityLogCreatedSubscriptionGQL = gql`
  subscription ActivityLogCreated($input: ActivityLogSubscriptionInput!) {
    activityLogCreated(input: $input) {
      id
      activityType
      createdAt
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TaskUpdatedSubscriptionGQL = gql`
  subscription TaskUpdated($input: TaskUpdatedSubscriptionInput!) {
    taskUpdated(input: $input) {
      taskId
      updateType
      delegateStatus
    }
  }
`;

interface Props {
  taskId: string;
  activityTypes?: ActivityType[];
  safetyPollActivityLogs?: boolean;
}

const shouldRefetchScheduleTaskForTaskUpdate = (updateType?: string | null) =>
  updateType != null &&
  [
    'INTERVIEW_PLAN',
    'AVAILABILITY',
    'CANDIDATE_AVAILABILITY',
    'AVAILABILITY_REQUEST',
    'SELF_SCHEDULE_REQUEST',
    'SCHEDULE',
  ].includes(updateType);

/**
 * Fetches and renders task activity logs, optionally safety polling while agent work is in flight.
 */
const TaskDetailsActivityLogsList: FC<Props> = ({ taskId, activityTypes, safetyPollActivityLogs = false }) => {
  const [hasLoadedAdditionalActivityLogPages, setHasLoadedAdditionalActivityLogPages] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const shouldSafetyPollActivityLogs = safetyPollActivityLogs && !hasLoadedAdditionalActivityLogPages;

  const { data, loading, error, fetchMore } = useTaskDetailsActivityLogsListQuery({
    variables: {
      taskId,
      input: {
        activityTypes,
        pageInput: {
          limit: PAGE_SIZE_100,
        },
      },
    },
    pollInterval: shouldSafetyPollActivityLogs ? AGENT_ACTIVITY_LOG_SAFETY_POLL_INTERVAL_MS : undefined,
  });

  // Real-time subscription for activity log updates on this task
  useActivityLogCreatedSubscription({
    variables: { input: { entityType: ActivityLogEntityType.ActivityLogEntityTask, entityId: taskId } },
    onData: ({ data: subData, client }) => {
      const newLog = subData.data?.activityLogCreated;
      if (!newLog) {
        return;
      }

      // Prepend the new activity log to the cached query result
      client.cache.modify({
        id: client.cache.identify({ __typename: 'Task', id: taskId }),
        fields: {
          activityLogs(existing = { items: [], nextCursor: null }) {
            const newRef = client.cache.writeFragment({
              fragment: gql`
                fragment NewActivityLog on ActivityLog {
                  id
                  activityType
                  createdAt
                }
              `,
              data: newLog,
            });
            return {
              ...existing,
              items: [newRef, ...existing.items],
            };
          },
        },
      });
    },
  });

  // Real-time subscription for task field updates (e.g., delegateStatus changes).
  useTaskUpdatedSubscription({
    variables: { input: { taskId } },
    onData: ({ data: subData, client }) => {
      const taskUpdated = subData.data?.taskUpdated;
      if (!taskUpdated) {
        return;
      }
      const delegateStatus =
        (taskUpdated as typeof taskUpdated & { delegateStatus?: string | null }).delegateStatus ?? null;

      const cacheId = client.cache.identify({ __typename: 'Task', id: taskId });
      if (cacheId && taskUpdated.updateType === 'DELEGATE_STATUS') {
        client.cache.modify({
          id: cacheId,
          fields: {
            delegateStatus() {
              return delegateStatus;
            },
          },
        });
        return;
      }

      if (taskUpdated.updateType === 'DELEGATE_TYPE') {
        client.refetchQueries({ include: ['ScheduleTask'] });
        return;
      }

      if (shouldRefetchScheduleTaskForTaskUpdate(taskUpdated.updateType)) {
        client.refetchQueries({ include: [...SCHEDULE_TASK_RELATED_QUERY_NAMES] });
      }
    },
  });

  // If we are not careful we get the following error:
  //   > Expression produces a union type that is too complex
  // We are getting around that by casting to any.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const options = (data?.task?.activityLogs?.items ?? []) as any[];

  const loadMoreData = async () => {
    if (!data?.task?.activityLogs?.nextCursor || loading || error || !fetchMore || isLoadingMore) {
      return;
    }

    setHasLoadedAdditionalActivityLogPages(true);
    setIsLoadingMore(true);

    try {
      await fetchMore({
        variables: {
          input: {
            pageInput: {
              limit: PAGER_DEFAULT,
              cursor: data?.task?.activityLogs?.nextCursor,
            },
          },
        },
        updateQuery: (previousQueryResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return previousQueryResult;
          }

          const newOptions = fetchMoreResult.task?.activityLogs.items || [];

          // The very explicit deep merge is needed because __typename is required for Apollo cache merging.
          // https://www.notion.so/modernloop/Writing-Client-GraphQL-539c431cad4b48d997de434ee2f8ba7e#fd1d0aa40d344d5ca16c11b25c3f730d
          const result = {
            ...previousQueryResult,
            ...fetchMoreResult,
            task: {
              ...previousQueryResult.task,
              ...fetchMoreResult.task,
              id: fetchMoreResult.task?.id,
              activityLogs: {
                ...previousQueryResult.task?.activityLogs,
                ...fetchMoreResult.task?.activityLogs,
                items: [...(previousQueryResult.task?.activityLogs.items || []), ...newOptions],
                nextCursor: fetchMoreResult.task?.activityLogs.nextCursor,
              },
            },
          };

          return result;
        },
      });
    } catch (paginationError) {
      logError(paginationError);
    } finally {
      setIsLoadingMore(false);
    }
  };

  if (loading && !data?.task?.activityLogs) {
    return <Loader loading />;
  }

  if (error) {
    return <PublicError error={error} />;
  }

  return (
    <ActivityLogsList
      activityLogs={options}
      isLoadingMore={isLoadingMore}
      onViewMoreClick={loadMoreData}
      showViewMoreButton={Boolean(data?.task?.activityLogs?.nextCursor)}
    />
  );
};

export default TaskDetailsActivityLogsList;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TaskDetailsActivityLogsListQueryGQL = gql`
  ${ActivityLogsList.fragments.activityLogs}
  fragment TaskDetailsActivityLogsList_activityLog on ActivityLog {
    id
    ...ActivityLogsList_activityLog
  }
  query TaskDetailsActivityLogsList(
    $taskId: uuid!
    $input: ActivityLogsForSourceInput!
    $showTaskName: Boolean! = false
  ) {
    task(id: $taskId) {
      id
      activityLogs(input: $input) {
        items {
          id
          ...TaskDetailsActivityLogsList_activityLog
        }
        nextCursor
      }
    }
  }
`;
